import React from 'react';
import {Helmet} from 'react-helmet-async';
import LogoTime2eat from '../assets/logo-time2eat.svg';
import './onboarding.scss';
import {Link, useLocation, useParams} from 'react-router-dom';
import searchFilter from '../utilities/search-filter';
import {MERCHANT_SERVICES} from './service-list';
import PhoneIcon from '../assets/icons0/phone.svg';
import {FormattedMessage} from 'react-intl';
import Button from './button/button';
import AccountsRegistration from '../assets/onboarding/accounts-registration.jpg';
import AccountsRegistration2x from '../assets/onboarding/accounts-registration@2x.jpg';
import ServiceListDesktop from '../assets/onboarding/service-list-desktop.jpg';
import ServiceListDesktop2x from '../assets/onboarding/service-list-desktop@2x.jpg';
import ServiceListMobile from '../assets/onboarding/service-list-mobile.jpg';
import ServiceListMobile2x from '../assets/onboarding/service-list-mobile@2x.jpg';
import QuestionMarkIcon from '../assets/onboarding/question-mark.svg';
import LogoText from '../assets/logo-text.svg';
import {useRedirectToSignIn} from './sign-in';

const Onboarding = () => {
    const {merchantId} = useParams();
    const location = useLocation();
    const support = MERCHANT_SERVICES[merchantId].support;
    const contentRef = React.useRef();
    const showGetStarted = React.useCallback(() => {
        if (contentRef && contentRef.current) {
            contentRef.current.scrollIntoView({
                behavior: "smooth",
            })
        }
    });
    const {redirectToSignIn} = useRedirectToSignIn({continueFrom: `/list/${merchantId}`});
    return (
        <>
            <Helmet>
                <title>Time to eat onboarding</title>
                <meta name='description' content='Time2eat'/>
            </Helmet>
            <div className='mm-onboarding'>
                <div className='mm-onboarding__hero'>
                    <div className='mw768-max-width-80rem mw768-margin-0-auto'>
                        <div className='mm-onboarding__header display-flex justify-content-space-between align-items-center'>
                            <Link to={`/${searchFilter(location.search)}`}>
                                <LogoTime2eat className='display-block' style={{width: 134, height: 40}} />
                            </Link>
                            <a className='display-block color-black' href={`tel:${support.number}`}>
                                <PhoneIcon className='display-block width-1dot5rem height-1dot5rem'/>
                            </a>
                        </div>
                        <div className='mm-onboarding__main'>
                            <h1 className='mm-onboarding__title color-grayscale-900 display-sm bold'>
                                <FormattedMessage defaultMessage='Register to use micromarkets'/>
                            </h1>
                            <p className='mm-onboarding__description text-md mw768-text-xl padding-top-0dot75rem mw768-padding-top-1dot25rem'>
                                <FormattedMessage defaultMessage='To purchase goods in Time2Eat micromarkets, you first need to complete a quick registration on the platform.'/>
                            </p>
                            <div className='mm-onboarding__action-container display-flex flex-direction-column row-gap-0dot75rem padding-top-1dot5rem mw768-padding-top-2dot5rem'>
                                <Button {...{color: 'orange', size: 'lg', clickHandler: redirectToSignIn}}>
                                    <FormattedMessage defaultMessage='Register'/>
                                </Button>
                                <Button {...{color: 'grayscale', size: 'lg', clickHandler: showGetStarted}}>
                                    <FormattedMessage defaultMessage='Get started'/>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
                <div ref={contentRef} className='mm-onboarding__content text-align-center'>
                    <h2 className='mm-onboarding__content-title color-grayscale-900 text-xl bold'>
                        <FormattedMessage defaultMessage='How to get started'/>
                    </h2>
                    <p className='mm-onboarding__content-description padding-top-0dot5rem mw768-padding-top-0dot75rem semibold text-sm padding-bottom-2rem mw768-padding-bottom-3rem'>
                        <FormattedMessage defaultMessage='How to start using platform'/>
                    </p>
                    <div className='mm-onboarding__box'>
                        <div className='mm-onboarding__box-order'>1</div>
                        <p className='mm-onboarding__box-title'>
                            <FormattedMessage defaultMessage='Registration on the platform'/>
                        </p>
                        <p className='mm-onboarding__box-description'>
                            <FormattedMessage defaultMessage='You can sign up using the Continue with Google button, or using your email'/>
                        </p>
                    </div>
                    <div className='mm-onboarding__img'>
                        <img src={AccountsRegistration} srcSet={`${AccountsRegistration}, ${AccountsRegistration2x} 2x`} className='display-block'/>
                    </div>
                    <div className='mm-onboarding__hint mm-onboarding__hint--bordered'>
                        <div className='mm-onboarding__hint-icon'>
                            <QuestionMarkIcon className='display-block'/>
                        </div>
                        <div className='mm-onboarding__hint-text'>
                            <span>
                                <FormattedMessage defaultMessage='If you choose to register via '/>
                            </span>
                            <span className='bold'>
                                {' '}<FormattedMessage defaultMessage='email'/>,
                            </span>
                            <span>
                                {' '}<FormattedMessage defaultMessage='your password will be sent to you via email and you will need to copy it'/>
                            </span>
                        </div>
                    </div>
                     <div className='mm-onboarding__box'>
                        <div className='mm-onboarding__box-order'>2</div>
                        <p className='mm-onboarding__box-title'>
                            <FormattedMessage defaultMessage='Micromarkets page'/>
                        </p>
                        <p className='mm-onboarding__box-description'>
                            <FormattedMessage defaultMessage='After registration you will be taken to the micromarkets page. Then choose the micromarket closest to you'/>
                        </p>
                    </div>
                    <div className='mm-onboarding__img'>
                        <img src={ServiceListDesktop} srcSet={`${ServiceListDesktop}, ${ServiceListDesktop2x} 2x`} className='display-block hide-mobile'/>
                        <img src={ServiceListMobile} srcSet={`${ServiceListMobile}, ${ServiceListMobile2x} 2x`} className='display-block hide-desktop'/>
                    </div>
                    <div className='mm-onboarding__hint'>
                        <div className='mm-onboarding__hint-icon'>
                             <QuestionMarkIcon className='display-block'/>
                        </div>
                        <div className='mm-onboarding__hint-text'>
                            <span>
                                <FormattedMessage defaultMessage='You can also access the micromarket via the'/>
                            </span>
                            <span className='bold'>
                                {' '}<FormattedMessage defaultMessage='QR-code'/>
                            </span>
                            <span>
                                {' '}<FormattedMessage defaultMessage='on it'/>
                            </span>
                        </div>
                    </div>
                    <div className='mm-onboarding__action-container display-flex flex-direction-column padding-top-2rem mw768-padding-top-3rem'>
                        <Button {...{color: 'orange', size: 'lg'}}>
                            <FormattedMessage defaultMessage='Register'/>
                        </Button>
                    </div>
                </div>
                <div className='mm-onboarding__footer display-flex justify-content-center align-items-center padding-top-2rem padding-bottom-1dot5rem'>
                    <p className='text-md medium color-gray-500'>
                        <FormattedMessage defaultMessage='Powered by'/>
                    </p>
                    <LogoText className='height-1dot125rem margin-left-0dot5rem width-auto'/>
                </div>
            </div>
        </>
    );
};

export default React.memo(Onboarding);
